import React, { createContext, useContext, useMemo, useReducer } from 'react';

const StoreCtx = createContext(null);

const initialState = {
  auth: {
    accessToken: localStorage.getItem('railcab_accessToken') || '',
    refreshToken: localStorage.getItem('railcab_refreshToken') || '',
    user: null,
  },
  pages: {
    list: [],
    byId: {},
  },
  ui: {
    loading: false,
    toast: null,
  },
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, ui: { ...state.ui, loading: action.payload } };

    case 'SET_TOAST':
      return { ...state, ui: { ...state.ui, toast: action.payload } };

    case 'SET_TOKENS': {
      const { accessToken, refreshToken } = action.payload;
      if (accessToken !== undefined) localStorage.setItem('railcab_accessToken', accessToken || '');
      if (refreshToken !== undefined) localStorage.setItem('railcab_refreshToken', refreshToken || '');
      return {
        ...state,
        auth: {
          ...state.auth,
          accessToken: accessToken ?? state.auth.accessToken,
          refreshToken: refreshToken ?? state.auth.refreshToken,
        },
      };
    }

    case 'SET_USER':
      return { ...state, auth: { ...state.auth, user: action.payload } };

    case 'LOGOUT':
      localStorage.removeItem('railcab_accessToken');
      localStorage.removeItem('railcab_refreshToken');
      return { ...state, auth: { accessToken: '', refreshToken: '', user: null }, pages: { list: [], byId: {} } };

    case 'SET_PAGES':
      return { ...state, pages: { ...state.pages, list: Array.isArray(action.payload) ? action.payload : [] } };

    case 'CACHE_PAGE': {
      const { id, page } = action.payload;
      return { ...state, pages: { ...state.pages, byId: { ...state.pages.byId, [id]: page } } };
    }

    default:
      return state;
  }
}

export function StoreProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const actions = useMemo(() => ({
    setLoading(v) { dispatch({ type: 'SET_LOADING', payload: !!v }); },
    toast(msg, kind = 'info') {
      dispatch({ type: 'SET_TOAST', payload: { msg, kind, at: Date.now() } });
      setTimeout(() => dispatch({ type: 'SET_TOAST', payload: null }), 3500);
    },
    setTokens(tokens) { dispatch({ type: 'SET_TOKENS', payload: tokens }); },
    setUser(user) { dispatch({ type: 'SET_USER', payload: user }); },
    logout() { dispatch({ type: 'LOGOUT' }); },
    setPages(list) { dispatch({ type: 'SET_PAGES', payload: list }); },
    cachePage(id, page) { dispatch({ type: 'CACHE_PAGE', payload: { id, page } }); },
  }), []);

  const value = useMemo(() => ({ state, actions }), [state, actions]);
  return <StoreCtx.Provider value={value}>{children}</StoreCtx.Provider>;
}

export function useStore() {
  const ctx = useContext(StoreCtx);
  if (!ctx) throw new Error('useStore must be used within StoreProvider');
  return ctx;
}
